// App-wide modal scroll lock (STORY-1063).
//
// Detection is OPT-OUT: every full-screen overlay — the `fixed inset-0` Tailwind pair or
// the legacy `models/Modal` `.overlay` class — locks page scrolling by default, and the
// handful of non-modal full-screen layers (transparent dropdown click-aways) carry an
// explicit `data-scroll-passthrough` attribute instead. Measured landscape when this was
// introduced: ~97 `fixed inset-0` backdrops + 32 legacy `.overlay` modals vs ~4
// click-aways. Opt-out therefore reaches app-wide coverage in a few edits, and a NEW
// modal is correct automatically — while a new dropdown that forgets the passthrough
// marker fails loudly (scrolling visibly locks) rather than silently. No structural
// heuristic can tell the two apart: modal dimming variously lives on the overlay itself,
// a child, a sibling BackgroundBlur, or the `.overlay` CSS class.
//
// Both lock layers are driven from this module:
//
//   1. While any locking overlay is mounted, <html> carries `modal-scroll-locked` and
//      App.css sets `overflow: hidden` on it — viewport scrollbar frozen, scroll position
//      untouched, stacked modals naturally hold the lock until the last one unmounts.
//      (Toggling the class from JS avoids depending on CSS :has() support.)
//   2. A capture-phase wheel/touchmove handler stops scrolling from chaining PAST a
//      backdrop into scrollable page panels underneath. CSS cannot do this: most modals
//      are not portaled, so the page panel is an ANCESTOR of the backdrop and the
//      browser's scroll-chaining walks straight to it. An event is allowed only when some
//      actually-scrollable element sits between the target and its enclosing overlay —
//      modal bodies, textareas, and dropdown lists keep scrolling; everything else is
//      swallowed. With no overlay mounted the handler is a cached-boolean no-op.

const LOCKING_OVERLAY_SELECTOR =
    '.fixed.inset-0:not([data-scroll-passthrough]):not(.pointer-events-none), ' +
    '.overlay:not([data-scroll-passthrough]):not(.pointer-events-none)';

const LOCKED_CLASS = 'modal-scroll-locked';

let installed = false;
let overlayPresent = false;
let observer = null;

const isScrollable = (el) => {
    const style = window.getComputedStyle(el);
    // overflow:auto/scroll is enough to recognise a designated scroll area — we do NOT
    // also require scrollHeight > clientHeight.  When content fits exactly the container
    // isn't overflowing at this instant, but it is still the correct target for scroll
    // gestures.  Any leftover delta is contained by the CSS overscroll-behavior:contain
    // rule already applied to all modal descendants (STORY-1063) without chaining to the
    // page.  The stricter height check caused BUG-1100: calendar day cells and list cards
    // inside .bail-schedule-modal-content blocked wheel events because the modal content
    // fit within the viewport and the container wasn't considered "scrollable".
    return (
        /(auto|scroll)/.test(style.overflowY) ||
        /(auto|scroll)/.test(style.overflowX)
    );
};

const hasScrollableBetween = (target, overlay) => {
    let el = target;
    while (el && el !== overlay) {
        if (isScrollable(el)) return true;
        el = el.parentElement;
    }
    return false;
};

const onScrollIntent = (event) => {
    if (!overlayPresent) return;
    // Ctrl+wheel is the browser zoom gesture, not a scroll.
    if (event.ctrlKey) return;

    const target = event.target instanceof Element ? event.target : null;
    const overlay = target?.closest(LOCKING_OVERLAY_SELECTOR) ?? null;

    // Backdrops cover the viewport, so while one is open the target is normally inside
    // it. Either way: only let the event through when it can scroll something that
    // belongs to the overlay.
    if (!overlay || !hasScrollableBetween(target, overlay)) {
        event.preventDefault();
    }
};

const updateOverlayPresence = () => {
    overlayPresent = !!document.querySelector(LOCKING_OVERLAY_SELECTOR);
    document.documentElement.classList.toggle(LOCKED_CLASS, overlayPresent);
};

export function installModalScrollLock() {
    if (installed || typeof document === 'undefined') return;
    installed = true;

    // Most overlays mount/unmount, but some toggle into existence by className (e.g.
    // CurfewAreaConfiguration's expand-map flips `relative` to `fixed inset-0`), and the
    // passthrough marker itself is an attribute — observe both kinds of change.
    observer = new MutationObserver(updateOverlayPresence);
    observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'data-scroll-passthrough'],
    });
    updateOverlayPresence();

    // Capture phase + passive:false so the handler can cancel the scroll before any
    // scrollable ancestor consumes it.
    document.addEventListener('wheel', onScrollIntent, { capture: true, passive: false });
    document.addEventListener('touchmove', onScrollIntent, { capture: true, passive: false });
}

// Test seam: tear everything down so each test installs fresh.
export function uninstallModalScrollLock() {
    if (!installed) return;
    installed = false;
    overlayPresent = false;
    document.documentElement.classList.remove(LOCKED_CLASS);
    observer?.disconnect();
    observer = null;
    document.removeEventListener('wheel', onScrollIntent, { capture: true });
    document.removeEventListener('touchmove', onScrollIntent, { capture: true });
}
